import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ThematicAnalysisState,
  ThemeGroup,
  ThemeData,
} from '@/types/thematicAnalysis';
import {
  fetchThematicGrouping,
  createAllThemeElements,
  regenerateThemeAnalysis,
  fetchAllThemeElements,
} from '../asyncActions/thematicAnalysisActions';

const initialState: ThematicAnalysisState = {
  // Grouping dialog state
  groupingLoading: false,
  suggestedGroups: [],
  groupingError: null,

  // Element creation state
  creatingElements: false,
  creationProgress: {
    total: 0,
    completed: 0,
    currentTheme: '',
  },

  // Theme data state
  themeElements: [],
  themeLoading: false,

  // Edit state
  editingTheme: null,
  regenerating: false,

  // Dialog states
  showGroupingDialog: false,
  showEditModal: false,
};

export const ThematicAnalysisSlice = createSlice({
  name: 'thematicAnalysis',
  initialState,
  reducers: {
    openGroupingDialog: (state) => {
      state.showGroupingDialog = true;
    },
    closeGroupingDialog: (state) => {
      state.showGroupingDialog = false;
      state.suggestedGroups = [];
      state.groupingError = null;
    },
    updateSuggestedGroups: (state, action: PayloadAction<ThemeGroup[]>) => {
      state.suggestedGroups = action.payload;
    },
    openEditModal: (state, action: PayloadAction<ThemeData>) => {
      state.editingTheme = action.payload;
      state.showEditModal = true;
    },
    closeEditModal: (state) => {
      state.editingTheme = null;
      state.showEditModal = false;
    },
    setThemeElements: (state, action: PayloadAction<ThemeData[]>) => {
      state.themeElements = action.payload;
    },
    updateThemeElement: (state, action: PayloadAction<ThemeData>) => {
      const index = state.themeElements.findIndex(
        (el) => el.elementId === action.payload.elementId,
      );
      if (index !== -1) {
        state.themeElements[index] = action.payload;
      }
    },
    clearThemeData: (state) => {
      state.themeElements = [];
      state.suggestedGroups = [];
      state.groupingError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch grouping
      .addCase(fetchThematicGrouping.pending, (state) => {
        state.groupingLoading = true;
        state.groupingError = null;
      })
      .addCase(fetchThematicGrouping.fulfilled, (state, action) => {
        state.groupingLoading = false;
        if (action.payload.status && action.payload.data) {
          state.suggestedGroups = action.payload.data.suggestedGroups || [];
          if (action.payload.data.error) {
            state.groupingError = action.payload.data.error;
          }
        } else {
          state.groupingError = 'Failed to get AI suggestions';
        }
      })
      .addCase(fetchThematicGrouping.rejected, (state, action) => {
        state.groupingLoading = false;
        state.groupingError = action.error.message || 'Failed to fetch groupings';
      })
      // Create all elements
      .addCase(createAllThemeElements.pending, (state, action) => {
        state.creatingElements = true;
        state.creationProgress = {
          total: action.meta.arg.themes.length,
          completed: 0,
          currentTheme: 'Creating elements...',
        };
      })
      .addCase(createAllThemeElements.fulfilled, (state) => {
        state.creatingElements = false;
        state.creationProgress = {
          total: 0,
          completed: 0,
          currentTheme: '',
        };
        state.showGroupingDialog = false;
      })
      .addCase(createAllThemeElements.rejected, (state) => {
        state.creatingElements = false;
      })
      // Fetch all theme elements
      .addCase(fetchAllThemeElements.pending, (state) => {
        state.themeLoading = true;
      })
      .addCase(fetchAllThemeElements.fulfilled, (state, action) => {
        state.themeLoading = false;
        // Transform the responses into ThemeData
        const elements: ThemeData[] = action.payload.map((response: any) => {
          const element = response.element || response;
          const filterObject = element.filterObject || {};
          const aiAnalysis = element.aiAnalysis || {};

          return {
            elementId: element.reportElementId || element.id,
            themeId: filterObject.themeId || '',
            themeName: filterObject.themeName || element.title || '',
            order: element.orderId || 0,
            questions: filterObject.questionIds?.map((qId: number) => ({
              questionId: qId,
              questionText: '',
              answerType: '',
            })) || [],
            userPreference: filterObject.userPreference || '',
            sentiment: aiAnalysis.sentiment || 'mixed',
            summary: aiAnalysis.summary || '',
            keywords: aiAnalysis.keywords || [],
          };
        });
        state.themeElements = elements;
      })
      .addCase(fetchAllThemeElements.rejected, (state) => {
        state.themeLoading = false;
      })
      // Regenerate theme analysis
      .addCase(regenerateThemeAnalysis.pending, (state) => {
        state.regenerating = true;
      })
      .addCase(regenerateThemeAnalysis.fulfilled, (state, action) => {
        state.regenerating = false;
        // Update the editing theme with new AI analysis
        if (state.editingTheme && action.payload.status && action.payload.data) {
          const updatedTheme = {
            ...state.editingTheme,
            sentiment: action.payload.data.sentiment,
            summary: action.payload.data.summary,
            keywords: action.payload.data.keywords,
          };
          state.editingTheme = updatedTheme;

          // Also update in themeElements array
          const index = state.themeElements.findIndex(
            (el) => el.elementId === state.editingTheme?.elementId,
          );
          if (index !== -1) {
            state.themeElements[index] = updatedTheme;
          }
        }
      })
      .addCase(regenerateThemeAnalysis.rejected, (state) => {
        state.regenerating = false;
      });
  },
});

export const { actions: thematicAnalysisActions } = ThematicAnalysisSlice;
export default ThematicAnalysisSlice.reducer;
